import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const getLogoPositionClasses = (position) => {
  switch (position) {
    case 'top-left': return 'top-4 left-4';
    case 'top-right': return 'top-4 right-4';
    case 'bottom-left': return 'bottom-16 left-4';
    case 'bottom-right':
    default: return 'bottom-16 right-4';
  }
};

const getLogoSizeClasses = (size) => {
  switch (size) {
    case 'small': return 'h-8 max-w-[120px]';
    case 'medium': return 'h-12 max-w-[180px]';
    case 'large': return 'h-16 max-w-[240px]';
    default: return 'h-8 max-w-[120px]';
  }
};

const getTextPositionClasses = (position) => {
  switch (position) {
    case 'bottom-left': return 'bottom-16 left-4';
    case 'bottom-right': return 'bottom-16 right-4';
    default: return 'bottom-16 left-1/2 transform -translate-x-1/2';
  }
};

export default function BrandingPreviewModal({ 
  open, 
  onClose, 
  project, 
  setProject,
  t 
}) {
  const hasLogo = project.branding_logo_url;
  const hasText = project.branding_text;
  const hasBranding = hasLogo || hasText;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('branding_preview') || 'Branding Preview'}</DialogTitle>
          <DialogDescription>
            {t('preview_description') || 'Preview how your branding will appear during presentations'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          {/* Simulated Slide Preview */}
          <div className="relative aspect-video bg-gradient-to-br from-slate-900 to-slate-800 rounded-lg overflow-hidden">
            {/* Sample slide content */}
            <div className="absolute inset-0 flex items-center justify-center">
              <p className="text-white text-2xl font-semibold text-center px-8">
                {t('sample_slide_title') || 'Sample Slide Title'}
              </p>
            </div>
            
            {/* Logo Preview */}
            {hasLogo && (
              <div 
                className={`absolute ${getLogoPositionClasses(project.branding_logo_position)}`}
                style={{ opacity: project.branding_logo_opacity || 0.8 }}
              >
                <img 
                  src={project.branding_logo_url} 
                  alt="Branding Logo"
                  className={`object-contain ${getLogoSizeClasses(project.branding_logo_size)}`}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
            
            {/* Text Preview */}
            {hasText && (
              <div 
                className={`absolute ${getTextPositionClasses(project.branding_text_position)} px-3 py-1 bg-black/30 rounded`}
                style={{ opacity: project.branding_logo_opacity || 0.8 }}
              >
                <span className="text-white text-sm font-medium">
                  {project.branding_text}
                </span>
              </div>
            )}
            
            {/* Hint when no branding configured */}
            {!hasBranding && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                <p className="text-white/70 text-sm">
                  {t('add_branding_hint') || 'Add a logo URL or branding text to see the preview'}
                </p>
              </div>
            )}
            
            {/* Simulated slide indicator */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
              <span className="text-white/50 text-xs">1 / 5</span>
            </div>
            
            {/* Simulated navigation */}
            <div className="absolute bottom-4 right-4 flex gap-2">
              <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
                <ChevronLeft className="w-4 h-4 text-white/50" />
              </div>
              <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
                <ChevronRight className="w-4 h-4 text-white/50" />
              </div>
            </div>
          </div>
          
          {/* Quick Settings */}
          {hasBranding && (
            <div className="mt-4 grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">{t('logo_position') || 'Position'}</Label>
                <Select
                  value={project.branding_logo_position || "bottom-right"}
                  onValueChange={(value) => setProject({ ...project, branding_logo_position: value })}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="top-left">{t('top_left') || 'Top Left'}</SelectItem>
                    <SelectItem value="top-right">{t('top_right') || 'Top Right'}</SelectItem>
                    <SelectItem value="bottom-left">{t('bottom_left') || 'Bottom Left'}</SelectItem>
                    <SelectItem value="bottom-right">{t('bottom_right') || 'Bottom Right'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs">{t('logo_size') || 'Size'}</Label>
                <Select
                  value={project.branding_logo_size || "small"}
                  onValueChange={(value) => setProject({ ...project, branding_logo_size: value })}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">{t('small') || 'Small'}</SelectItem>
                    <SelectItem value="medium">{t('medium') || 'Medium'}</SelectItem>
                    <SelectItem value="large">{t('large') || 'Large'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs">{t('logo_opacity') || 'Opacity'}: {Math.round((project.branding_logo_opacity || 0.8) * 100)}%</Label>
                <Slider
                  value={[project.branding_logo_opacity || 0.8]}
                  min={0.2}
                  max={1}
                  step={0.1}
                  onValueChange={([value]) => setProject({ ...project, branding_logo_opacity: value })}
                  className="mt-2"
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button onClick={onClose}>
            {t('close_preview') || 'Close Preview'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
